/**
 * ReportGeneratorPage - Generate and preview status reports
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  reportsApi,
  projectsApi,
  type Report,
  type ReportType,
  type ReportTypeInfo,
  type GenerateReportRequest,
  type Project,
} from '@services/api';
import { useAuthStore } from '@stores/authStore';
import { ReportPreview } from '@components/reports/ReportPreview';
import { ReportScheduleSettings } from '@components/reports/ReportScheduleSettings';

type DateRangeOption = '7d' | '14d' | '30d' | '90d' | 'custom';

export default function ReportGeneratorPage() {
  const { t } = useTranslation();
  const currentOrganization = useAuthStore((state) => state.currentOrganization);
  const [searchParams] = useSearchParams();

  // State
  const [reportTypes, setReportTypes] = useState<ReportTypeInfo[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedType, setSelectedType] = useState<ReportType>('project_status');
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [dateRange, setDateRange] = useState<DateRangeOption>('30d');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [includeAiSummary, setIncludeAiSummary] = useState(true);

  const [report, setReport] = useState<Report | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialDataError, setInitialDataError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<'generate' | 'schedule'>('generate');
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  // Pre-fill from URL params
  useEffect(() => {
    const projectIdParam = searchParams.get('projectId');
    const typeParam = searchParams.get('type') as ReportType | null;

    if (projectIdParam) {
      setSelectedProjectId(projectIdParam);
    }
    if (typeParam) {
      setSelectedType(typeParam);
    }
  }, [searchParams]);

  // Load report types and projects
  const loadInitialData = useCallback(async () => {
    if (!currentOrganization) return;

    setInitialDataError(null);

    try {
      const [typesData, projectsData] = await Promise.all([
        reportsApi.getReportTypes(),
        projectsApi.list(currentOrganization.id),
      ]);

      setReportTypes(typesData);
      setProjects(projectsData.items || []);
    } catch (err) {
      console.error('Failed to load initial data:', err);
      setInitialDataError(t('reports.loadDataError', 'Failed to load report options. Please try again.'));
    }
  }, [currentOrganization, t]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Calculate date range
  const getDateRange = useCallback((): { startDate: Date | null; endDate: Date | null } => {
    const now = new Date();
    const endDate = now;

    if (dateRange === 'custom') {
      return {
        startDate: customStartDate ? new Date(customStartDate) : null,
        endDate: customEndDate ? new Date(customEndDate) : null,
      };
    }

    const daysMap: Record<DateRangeOption, number> = {
      '7d': 7,
      '14d': 14,
      '30d': 30,
      '90d': 90,
      custom: 0,
    };

    const days = daysMap[dateRange];
    const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

    return { startDate, endDate };
  }, [dateRange, customStartDate, customEndDate]);

  // Generate report
  const handleGenerateReport = async () => {
    if (!currentOrganization) return;

    setIsLoading(true);
    setError(null);

    try {
      const { startDate, endDate } = getDateRange();

      const request: GenerateReportRequest = {
        reportType: selectedType,
        organizationId: currentOrganization.id,
        projectId: selectedProjectId || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        includeAiSummary,
      };

      const reportData = await reportsApi.generateReport(request);
      setReport(reportData);
    } catch (err) {
      console.error('Failed to generate report:', err);
      setError(t('reports.generateError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Export to PDF
  const handleExportPdf = async () => {
    if (!currentOrganization) return;

    setIsExporting(true);

    try {
      const { startDate, endDate } = getDateRange();

      const request: GenerateReportRequest = {
        reportType: selectedType,
        organizationId: currentOrganization.id,
        projectId: selectedProjectId || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        includeAiSummary,
      };

      await reportsApi.downloadReportPdf(request);
    } catch (err) {
      console.error('Failed to export PDF:', err);
      setError(t('reports.exportError'));
    } finally {
      setIsExporting(false);
    }
  };

  // Get health score color
  const getHealthScoreColor = (score: number): string => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  if (!currentOrganization) {
    return (
      <div className="p-6 text-center text-neutral-500">
        {t('reports.noOrganization')}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
          {t('reports.title')}
        </h1>
        <p className="mt-1 text-sm text-neutral-500">
          {t('reports.subtitle')}
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-neutral-200 dark:border-neutral-700">
        <nav className="flex gap-8">
          <button
            onClick={() => setActiveTab('generate')}
            className={`pb-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'generate'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'
            }`}
          >
            {t('reports.tabs.generate')}
          </button>
          <button
            onClick={() => setActiveTab('schedule')}
            className={`pb-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'schedule'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'
            }`}
          >
            {t('reports.tabs.schedule')}
          </button>
        </nav>
      </div>

      {/* Initial data load error */}
      {initialDataError && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-between gap-4">
          <p className="text-sm text-red-600 dark:text-red-400">{initialDataError}</p>
          <button
            onClick={() => loadInitialData()}
            className="shrink-0 px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            {t('common.retry', 'Retry')}
          </button>
        </div>
      )}

      {/* Generate Tab */}
      {activeTab === 'generate' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Configuration Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-6">
                {t('reports.configuration')}
              </h2>

              {/* Report Type */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  {t('reports.reportType')}
                </label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value as ReportType)}
                  className="w-full px-4 py-2.5 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {reportTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                {reportTypes.find((t) => t.id === selectedType)?.description && (
                  <p className="mt-1 text-xs text-neutral-500">
                    {reportTypes.find((t) => t.id === selectedType)?.description}
                  </p>
                )}
              </div>

              {/* Project Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  {t('reports.projectFilter')}
                </label>
                <select
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">{t('reports.allProjects')}</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name} ({project.key})
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Range */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  {t('reports.dateRange')}
                </label>
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value as DateRangeOption)}
                  className="w-full px-4 py-2.5 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="7d">{t('reports.last7Days')}</option>
                  <option value="14d">{t('reports.last14Days')}</option>
                  <option value="30d">{t('reports.last30Days')}</option>
                  <option value="90d">{t('reports.last90Days')}</option>
                  <option value="custom">{t('reports.customRange')}</option>
                </select>

                {dateRange === 'custom' && (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-neutral-500 mb-1">
                        {t('reports.startDate')}
                      </label>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="w-full px-3 py-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-neutral-500 mb-1">
                        {t('reports.endDate')}
                      </label>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="w-full px-3 py-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* AI Summary Toggle */}
              <div className="mb-6">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeAiSummary}
                    onChange={(e) => setIncludeAiSummary(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                      {t('reports.includeAiSummary')}
                    </span>
                    <p className="text-xs text-neutral-500">
                      {t('reports.aiSummaryDescription')}
                    </p>
                  </div>
                </label>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleGenerateReport}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="w-4 h-4 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      {t('reports.generating')}
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      {t('reports.generateReport')}
                    </>
                  )}
                </button>

                {report && (
                  <button
                    onClick={handleExportPdf}
                    disabled={isExporting}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 border border-neutral-200 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isExporting ? (
                      <>
                        <svg
                          className="w-4 h-4 animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        {t('reports.exporting')}
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                          />
                        </svg>
                        {t('reports.exportPdf')}
                      </>
                    )}
                  </button>
                )}

                <button
                  onClick={() => setShowScheduleModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  {t('reports.scheduleReport')}
                </button>
              </div>
            </div>
          </div>

          {/* Report Preview Panel */}
          <div className="lg:col-span-2">
            {isLoading ? (
              <div className="flex items-center justify-center h-96 bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-neutral-500">{t('reports.generatingReport')}</p>
                </div>
              </div>
            ) : report ? (
              <ReportPreview
                report={report}
                getHealthScoreColor={getHealthScoreColor}
              />
            ) : (
              <div className="flex items-center justify-center h-96 bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700">
                <div className="text-center">
                  <svg
                    className="w-16 h-16 mx-auto text-neutral-300 dark:text-neutral-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <h3 className="mt-4 text-lg font-medium text-neutral-900 dark:text-white">
                    {t('reports.noReportYet')}
                  </h3>
                  <p className="mt-1 text-sm text-neutral-500">
                    {t('reports.configureAndGenerate')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Schedule Tab */}
      {activeTab === 'schedule' && (
        <ReportScheduleSettings
          organizationId={currentOrganization.id}
          projects={projects}
          reportTypes={reportTypes}
        />
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => setShowScheduleModal(false)}
            />
            <div className="relative bg-white dark:bg-neutral-800 rounded-xl shadow-xl max-w-md w-full p-6">
              <button
                onClick={() => setShowScheduleModal(false)}
                className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <ReportScheduleSettings
                organizationId={currentOrganization.id}
                projects={projects}
                reportTypes={reportTypes}
                defaultReportType={selectedType}
                defaultProjectId={selectedProjectId}
                onClose={() => setShowScheduleModal(false)}
                compact
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
